import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  ShieldCheck,
  ChevronRight,
  Activity,
  TriangleAlert,
  LayoutGrid,
} from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { tenantAdminService, TenantSummary, TenantHealthReport } from '@/features/branding/services/tenant-admin.service';
import { FeatureFlag } from '@/features/branding/services/feature-flags.service';

export const Route = createFileRoute('/_authenticated/admin/system-tenants')({
  component: SystemTenantsRoute,
});

function SystemTenantsRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_SYSTEM}>
      <SystemTenantsPage />
    </PermissionGate>
  );
}

function SystemTenantsPage() {
  const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null);
  const [detailTab, setDetailTab] = useState<'features' | 'health'>('features');

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => tenantAdminService.listTenants(),
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Carregando tenants...</div>;
  }

  const active = tenants.filter((t) => t.active);
  const inactive = tenants.filter((t) => !t.active);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" />
          Painel Super Admin — Tenants
        </h1>
        <p className="text-muted-foreground mt-1">
          Gerencie todos os clientes contratantes e seus módulos habilitados.
        </p>
      </div>

      <div className="flex gap-3">
        <Badge variant="default" className="text-sm px-3 py-1">
          {active.length} ativos
        </Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">
          {inactive.length} inativos
        </Badge>
        <Badge variant="secondary" className="text-sm px-3 py-1">
          {tenants.length} total
        </Badge>
      </div>

      <div className="grid gap-3">
        {tenants.map((tenant) => (
          <TenantCard
            key={tenant.tenantId}
            tenant={tenant}
            onOpen={() => {
              setSelectedTenant(tenant);
              setDetailTab('features');
            }}
          />
        ))}
      </div>

      {selectedTenant && (
        <TenantDetailDialog
          tenant={selectedTenant}
          tab={detailTab}
          onTabChange={setDetailTab}
          onClose={() => setSelectedTenant(null)}
        />
      )}
    </div>
  );
}

function TenantCard({
  tenant,
  onOpen,
}: {
  tenant: TenantSummary;
  onOpen: () => void;
}) {
  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onOpen}
    >
      <CardContent className="flex items-center justify-between py-4 px-5">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
            <Building2 className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium leading-none">{tenant.tenantName}</p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">{tenant.tenantSlug}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={tenant.active ? 'default' : 'secondary'}>
            {tenant.active ? 'Ativo' : 'Inativo'}
          </Badge>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

function TenantDetailDialog({
  tenant,
  tab,
  onTabChange,
  onClose,
}: {
  tenant: TenantSummary;
  tab: 'features' | 'health';
  onTabChange: (t: 'features' | 'health') => void;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {tenant.tenantName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 border-b pb-3">
          <Button
            variant={tab === 'features' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onTabChange('features')}
            className="flex items-center gap-1.5"
          >
            <LayoutGrid className="h-4 w-4" /> Módulos
          </Button>
          <Button
            variant={tab === 'health' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onTabChange('health')}
            className="flex items-center gap-1.5"
          >
            <Activity className="h-4 w-4" /> Saúde
          </Button>
        </div>

        {tab === 'features' && <FeaturesPanel tenantId={tenant.tenantId} />}
        {tab === 'health' && <HealthPanel tenantId={tenant.tenantId} />}
      </DialogContent>
    </Dialog>
  );
}

function FeaturesPanel({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();

  const { data: flags = [], isLoading } = useQuery({
    queryKey: ['admin', 'features', tenantId],
    queryFn: () => tenantAdminService.listFeatures(tenantId),
  });

  const toggleMutation = useMutation({
    mutationFn: (flag: FeatureFlag) =>
      tenantAdminService.upsertFeature(tenantId, {
        featureKey: flag.featureKey,
        isEnabled: !flag.isEnabled,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'features', tenantId] }),
    onError: () => toast.error('Erro ao atualizar módulo'),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Carregando módulos...</p>;

  const enabled = flags.filter((f) => f.isEnabled).length;

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {enabled} de {flags.length} módulos habilitados
      </p>
      <div className="divide-y rounded-lg border">
        {flags.map((flag) => (
          <div key={flag.featureKey} className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-sm font-medium">{flag.label}</p>
              <p className="text-xs text-muted-foreground font-mono">{flag.featureKey}</p>
            </div>
            <Switch
              checked={flag.isEnabled}
              disabled={toggleMutation.isPending}
              onCheckedChange={() => toggleMutation.mutate(flag)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function HealthPanel({ tenantId }: { tenantId: string }) {
  const { data: health, isLoading } = useQuery<TenantHealthReport>({
    queryKey: ['admin', 'health', tenantId],
    queryFn: () => tenantAdminService.getTenantHealth(tenantId),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground py-4">Carregando dados...</p>;
  if (!health) return null;

  const hasIntegrityIssues =
    health.integrity.usersWithoutRole > 0 || health.integrity.orphanedPermissions > 0;

  const counts: { label: string; value: number; icon: React.ReactNode }[] = [
    { label: 'Usuários', value: health.counts.users, icon: <Users className="h-4 w-4" /> },
    { label: 'Times', value: health.counts.teams, icon: <Users className="h-4 w-4" /> },
    { label: 'Clientes', value: health.counts.clients, icon: <Building2 className="h-4 w-4" /> },
    { label: 'Assets', value: health.counts.assets, icon: <LayoutGrid className="h-4 w-4" /> },
    { label: 'Roles', value: health.counts.roles, icon: <ShieldCheck className="h-4 w-4" /> },
    { label: 'Senhas', value: health.counts.passwords, icon: <ShieldCheck className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-2">
        {counts.map((c) => (
          <div key={c.label} className="rounded-lg border p-3 text-center">
            <p className="text-2xl font-bold">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <Card className={hasIntegrityIssues ? 'border-yellow-500/50' : 'border-green-500/50'}>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            {hasIntegrityIssues ? (
              <TriangleAlert className="h-4 w-4 text-yellow-500" />
            ) : (
              <ShieldCheck className="h-4 w-4 text-green-500" />
            )}
            Integridade de dados
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Usuários sem permissão</span>
            <Badge variant={health.integrity.usersWithoutRole > 0 ? 'destructive' : 'outline'}>
              {health.integrity.usersWithoutRole}
            </Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Permissões órfãs</span>
            <Badge variant={health.integrity.orphanedPermissions > 0 ? 'destructive' : 'outline'}>
              {health.integrity.orphanedPermissions}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-right">
        Verificado em {new Date(health.checkedAt).toLocaleString('pt-BR')}
      </p>
    </div>
  );
}
